import { useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { EmailAuthForm, GoogleIcon } from "@/components/AuthForms";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const HERO = "https://images.unsplash.com/photo-1586023492125-27b2c045efd7?crop=entropy&cs=srgb&fm=jpg&q=80&w=1600";

export default function Login() {
  const { user, setUser, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(location.state?.error || null);
  const from = location.state?.from || "/dashboard";

  if (!loading && user) return <Navigate to={from} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      const r = await api.post(mode === "login" ? "/auth/login" : "/auth/register", mode === "login" ? { email: form.email, password: form.password } : form);
      setUser(r.data);
      toast.success(mode === "login" ? "Welcome back" : "Account created");
      navigate(from, { replace: true, state: { user: r.data } });
    } catch (err) { setError(apiError(err)); }
    setBusy(false);
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2" style={{ background: "var(--bg)" }} data-testid="login-page">
      <div className="hidden lg:block relative overflow-hidden">
        <img src={HERO} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/30" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <div className="eyebrow text-white/70 mb-3">Moodboard Narrators</div>
          <p className="font-serif-editorial text-4xl leading-tight">Every client brief has a story. We help you narrate it in materials, light and colour.</p>
        </div>
      </div>
      <div className="flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-[420px]">
          <Link to="/" className="block mb-10" data-testid="login-brand">
            <div className="eyebrow mb-2">Atelier</div>
            <div className="font-serif-editorial text-3xl leading-none">Moodboard Narrators</div>
          </Link>
          <h1 className="editorial-heading text-4xl mb-2">{mode === "login" ? "Sign in" : "Create your studio account"}</h1>
          <p className="text-sm text-stone-500 mb-8">Sign-in is required so your projects, mood boards and brand stay private to your studio.</p>

          <button type="button" onClick={google} data-testid="login-google-btn" className="w-full inline-flex items-center justify-center gap-3 border border-stone-300 bg-white px-4 py-3 text-sm hover:border-black transition-colors">
            <GoogleIcon /> Continue with Google
          </button>
          <div className="flex items-center gap-4 my-6"><div className="hairline flex-1" /><span className="eyebrow">or with email</span><div className="hairline flex-1" /></div>

          <EmailAuthForm mode={mode} form={form} setForm={setForm} error={error} busy={busy} onSubmit={submit} />

          <div className="mt-6 text-sm text-stone-500">
            {mode === "login" ? "New to the studio?" : "Already have an account?"}{" "}
            <button type="button" onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(null); }} className="underline hover:text-black" data-testid="login-toggle-mode">
              {mode === "login" ? "Create an account" : "Sign in"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
